// ds-typo-ignore-file — mockup simulates report viewer UI, not marketing site DS
"use client"
import { blobMediaUrl } from "@/lib/blob-url"

/**
 * Static recreation of the report app nav bar.
 * Pixel-exact values extracted from my.aheadhealth.com, scaled to ~87% for iPad mockup.
 */

import { m } from "framer-motion"
import Image from "next/image"
import { useLocale } from "next-intl"
import { useTranslations } from "@/lib/intl/use-translations"
import { MockupDate } from "@/lib/mockup-date"
import { createViewportFadeUp } from "@/lib/motion"

const anim = (delay: number) => createViewportFadeUp(delay, { y: 10 })

export function ReportMockupNav() {
  const t = useTranslations("report")
  const locale = useLocale()
  return (
    <m.div {...anim(0.5)} className="flex flex-col px-3 pt-2 md:px-4">
      {/* Top nav bar */}
      <div className="flex items-center justify-between">
        {/* Left: logo + pill tabs */}
        <div className="flex items-center gap-2">
          {/* Ahead gradient logo */}
          <Image
            src={blobMediaUrl("/images/logos/ahead-logo-grad.svg")}
            alt=""
            width={24}
            height={24}
            className="h-6 w-6 rounded-full"
          />
          {/* Nav tabs — hidden on very small widths */}
          <div className="hidden items-center gap-1 sm:flex">
            <span className="text-foreground/60 rounded-full px-3 py-1 text-[12px] transition-colors duration-[250ms] hover:bg-neutral-100/30">
              {t("mockupNavHome")}
            </span>
            <span className="text-foreground rounded-full bg-neutral-100 px-3 py-1 text-[12px] font-medium transition-colors duration-[250ms] hover:bg-neutral-100">
              {t("mockupNavHealthReport")}
            </span>
            <span className="text-foreground/60 rounded-full px-3 py-1 text-[12px] transition-colors duration-[250ms] hover:bg-neutral-100/30">
              {t("mockupNavActionPlan")}
            </span>
          </div>
        </div>
        {/* Right: hamburger (small mockup) + my account button + avatar */}
        <div className="flex items-center gap-1.5 rounded-full border border-neutral-100 bg-white px-2.5 py-1">
          {/* Hamburger — visible on small mockup sizes */}
          <svg
            width="14"
            height="14"
            viewBox="0 0 16 8"
            fill="none"
            className="sm:hidden"
          >
            <line
              x1="0.75"
              y1="7.25"
              x2="15.25"
              y2="7.25"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
            <line
              x1="0.75"
              y1="1.25"
              x2="15.25"
              y2="1.25"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
          <span className="text-foreground hidden text-[12px] sm:inline">
            {t("mockupMyAccount")}
          </span>
          <span
            className="flex h-6 w-6 items-center justify-center rounded-full"
            style={{ backgroundColor: "rgba(115, 155, 210, 0.5)" }} // ds-color-ignore: translucent overlay
          >
            <Image
              src={blobMediaUrl("/images/report/account-avatar.svg")}
              alt=""
              width={20}
              height={20}
              className="h-5 w-5"
            />
          </span>
        </div>
      </div>
      {/* Separator */}
      <div className="mt-1.5 border-b border-neutral-100" />
      {/* Page title + report info */}
      <div className="mt-2 mb-2 flex items-start justify-between">
        <div>
          <div className="flex items-center gap-1">
            <span className="text-foreground text-[21px] font-normal">
              {t("mockupReportTitle")}
            </span>
            {/* Chevron — visible on mobile to indicate report switcher */}
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              className="text-foreground/40 mt-1 sm:hidden"
            >
              <path
                d="M3 4.5L6 7.5L9 4.5"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          {/* Report subtitle — visible on mobile */}
          <div className="text-foreground/50 mt-0.5 flex items-center gap-1.5 text-[10px] sm:hidden">
            <span>{t("mockupReportVariant")}</span>
            <span>|</span>
            <MockupDate locale={locale} />
          </div>
        </div>
        {/* Info + download icons — visible on mobile */}
        <div className="mt-1 flex items-center gap-1.5 sm:hidden">
          <div className="flex h-6 w-6 items-center justify-center rounded-full border border-neutral-100">
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <circle
                cx="5"
                cy="5"
                r="4"
                stroke="#2d2d2d"
                strokeWidth="0.8"
                strokeOpacity="0.4"
              />
              <path
                d="M5 4V6.5"
                stroke="#2d2d2d"
                strokeWidth="0.8"
                strokeOpacity="0.4"
                strokeLinecap="round"
              />
              <circle cx="5" cy="3" r="0.4" fill="#2d2d2d" fillOpacity="0.4" />
            </svg>
          </div>
          <div className="flex h-6 w-6 items-center justify-center rounded-full border border-neutral-100">
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <path
                d="M5 2v4.5M3 5l2 2 2-2"
                stroke="#2d2d2d"
                strokeWidth="0.8"
                strokeOpacity="0.4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M2 8.5h6"
                stroke="#2d2d2d"
                strokeWidth="0.8"
                strokeOpacity="0.4"
                strokeLinecap="round"
              />
            </svg>
          </div>
        </div>
      </div>
    </m.div>
  )
}
